import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

import { useStakingActions, useStakingData, useWalletConnection } from '../hooks';
import {
  ClaimableSummary,
  DepositActionParams,
  StakeParams,
  StakingContextValue,
  StakingKPIs,
  StakingPhaseData_V2,
  TransactionState,
  UserDeposit,
  VipPhaseData,
} from '../types';

const StakingDataContext = createContext<StakingContextValue | null>(null);

interface StakingDataProviderProps {
  children: ReactNode;
}

export const StakingDataProvider = ({ children }: StakingDataProviderProps) => {
  const wallet = useWalletConnection();
  const stakingData = useStakingData();
  const stakingActions = useStakingActions(stakingData.refetch);

  const stake = useCallback(
    async (params: StakeParams): Promise<void> => {
      if (!stakingData.currency) {
        console.error('Currency not available for staking');
        return;
      }
      await stakingActions.stake(params, stakingData.currency);
    },
    [stakingActions, stakingData.currency]
  );

  const stakeVip = useCallback(
    async (params: StakeParams): Promise<void> => {
      if (!stakingData.currency) {
        console.error('Currency not available for VIP staking');
        return;
      }
      await stakingActions.stakeVip(params, stakingData.currency);
    },
    [stakingActions, stakingData.currency]
  );

  const claim = useCallback(
    async (params: DepositActionParams): Promise<void> => {
      await stakingActions.claim(params);
    },
    [stakingActions]
  );

  const claimAll = useCallback(async (): Promise<void> => {
    await stakingActions.claimAll(
      stakingData.claimableSummary.periodicalClaimableCount,
      stakingData.claimableSummary.whitelistedClaimableCount,
      stakingData.claimableSummary.vipClaimableCount
    );
  }, [stakingActions, stakingData.claimableSummary]);

  const withdraw = useCallback(
    async (params: DepositActionParams): Promise<void> => {
      await stakingActions.withdraw(params);
    },
    [stakingActions]
  );

  const refetch = useCallback(async (): Promise<void> => {
    await stakingData.refetch();
  }, [stakingData]);

  const value = useMemo<StakingContextValue>(
    () => ({
      // Loading state
      isLoading: stakingData.isLoading,
      isInitialized: stakingData.isInitialized,
      error: stakingData.error,

      // Wallet data
      isWalletConnected: wallet.isConnected,
      walletAddress: wallet.address,
      isCorrectChain: wallet.isCorrectChain,

      // KPIs
      kpis: stakingData.kpis,

      // STAKE tab data
      stakingPhases: stakingData.stakingPhases,
      currentPhaseIndex: stakingData.currentPhaseIndex,

      // VIP tab data
      vipPhases: stakingData.vipPhases,
      currentVipPhaseIndex: stakingData.currentVipPhaseIndex,

      // EARNINGS tab data
      deposits: stakingData.deposits,
      claimableSummary: stakingData.claimableSummary,

      // Token info
      tokenDecimals: stakingData.tokenDecimals,
      tokenSymbol: stakingData.tokenSymbol,
      minDeposit: stakingData.minDeposit,
      minDepositDisplay: stakingData.minDepositDisplay,

      // Actions
      refetch,
      connectWallet: wallet.connectWallet,
      switchNetwork: wallet.switchNetwork,
      stake,
      stakeVip,
      claim,
      claimAll,
      withdraw,

      // Transaction state
      transaction: stakingActions.transaction,
      clearTransaction: stakingActions.clearTransaction,
    }),
    [
      stakingData,
      wallet,
      stakingActions.transaction,
      stakingActions.clearTransaction,
      refetch,
      stake,
      stakeVip,
      claim,
      claimAll,
      withdraw,
    ]
  );

  return <StakingDataContext.Provider value={value}>{children}</StakingDataContext.Provider>;
};

export const useStakingContext = (): StakingContextValue => {
  const context = useContext(StakingDataContext);

  if (!context) {
    throw new Error('useStakingContext must be used within StakingDataProvider');
  }

  return context;
};

/**
 * Hook to access only KPIs data
 */
export const useStakingKPIs = (): StakingKPIs => {
  const context = useStakingContext();
  return context.kpis;
};

/**
 * Hook to access only staking phases
 */
export const useStakingPhases = (): {
  phases: StakingPhaseData_V2[];
  currentPhaseIndex: number;
} => {
  const context = useStakingContext();
  return {
    phases: context.stakingPhases,
    currentPhaseIndex: context.currentPhaseIndex,
  };
};

/**
 * Hook to access VIP phases data
 */
export const useVipPhases = (): {
  vipPhases: VipPhaseData[];
  currentVipPhaseIndex: number;
} => {
  const context = useStakingContext();
  return {
    vipPhases: context.vipPhases,
    currentVipPhaseIndex: context.currentVipPhaseIndex,
  };
};

/**
 * Hook to access only deposits data
 */
export const useDeposits = (): {
  deposits: UserDeposit[];
  claimableSummary: ClaimableSummary;
} => {
  const context = useStakingContext();
  return {
    deposits: context.deposits,
    claimableSummary: context.claimableSummary,
  };
};

/**
 * Hook to access only transaction state
 */
export const useStakingTransaction = (): {
  transaction: TransactionState;
  clearTransaction: () => void;
} => {
  const context = useStakingContext();
  return {
    transaction: context.transaction,
    clearTransaction: context.clearTransaction,
  };
};

/**
 * Hook to access wallet state
 */
export const useStakingWallet = (): {
  isConnected: boolean;
  address: string | null;
  isCorrectChain: boolean;
  connectWallet: () => void;
  switchNetwork: () => void;
} => {
  const context = useStakingContext();
  return {
    isConnected: context.isWalletConnected,
    address: context.walletAddress,
    isCorrectChain: context.isCorrectChain,
    connectWallet: context.connectWallet,
    switchNetwork: context.switchNetwork,
  };
};

/**
 * Hook to access staking actions only
 */
export const useStakingActionsContext = (): {
  stake: (params: StakeParams) => Promise<void>;
  stakeVip: (params: StakeParams) => Promise<void>;
  claim: (params: DepositActionParams) => Promise<void>;
  claimAll: () => Promise<void>;
  withdraw: (params: DepositActionParams) => Promise<void>;
  refetch: () => Promise<void>;
} => {
  const context = useStakingContext();
  return {
    stake: context.stake,
    stakeVip: context.stakeVip,
    claim: context.claim,
    claimAll: context.claimAll,
    withdraw: context.withdraw,
    refetch: context.refetch,
  };
};
